
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Users, Video, Package, DollarSign, TrendingUp, AlertTriangle, Settings, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function AdminDashboardPage() {
  return (
    <div className="container-custom py-8">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold">Admin Dashboard</h1>
          <p className="text-muted-foreground">
            Platform management and analytics
          </p>
        </div>
        <div className="mt-4 md:mt-0 flex space-x-2">
          <Button variant="outline">
            <Settings className="h-4 w-4 mr-2" />
            Settings
          </Button>
          <Button>
            <Shield className="h-4 w-4 mr-2" />
            Security
          </Button>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Users</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="text-2xl font-bold">1,248</div>
              <Users className="h-5 w-5 text-primary-500" />
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              <span className="text-green-500">+12%</span> from last month
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Active Streams</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="text-2xl font-bold">24</div>
              <Video className="h-5 w-5 text-primary-500" />
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              <span className="text-green-500">+8%</span> from last week
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">NFT Sales</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="text-2xl font-bold">5,280 DUSD</div>
              <Package className="h-5 w-5 text-primary-500" />
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              <span className="text-green-500">+24%</span> from last month
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Platform Revenue</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="text-2xl font-bold">528 DUSD</div>
              <DollarSign className="h-5 w-5 text-primary-500" />
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              <span className="text-green-500">+18%</span> from last month
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <Tabs defaultValue="overview">
            <TabsList className="mb-6">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="users">Users</TabsTrigger>
              <TabsTrigger value="content">Content</TabsTrigger>
              <TabsTrigger value="transactions">Transactions</TabsTrigger>
            </TabsList>

            <TabsContent value="overview">
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle>Platform Activity</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px] flex items-center justify-center">
                    <TrendingUp className="h-16 w-16 text-primary-500/20" />
                  </div>
                </CardContent>
              </Card>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Recent Signups</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {[1, 2, 3, 4].map((i) => (
                        <div key={i} className="flex items-center">
                          <div className="w-10 h-10 rounded-full bg-dark-700 mr-3"></div>
                          <div>
                            <p className="font-medium">User{i}</p>
                            <p className="text-xs text-muted-foreground">
                              {i} hour{i !== 1 ? 's' : ''} ago
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Recent Streams</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {[1, 2, 3, 4].map((i) => (
                        <div key={i} className="flex items-center">
                          <div className="w-10 h-10 rounded-lg bg-dark-700 mr-3"></div>
                          <div>
                            <p className="font-medium">Stream #{i}</p>
                            <p className="text-xs text-muted-foreground">
                              by Creator{i} • {i * 10} viewers
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="users">
              <Card>
                <CardHeader>
                  <CardTitle>User Management</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="rounded-md border">
                    <div className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className="w-10 h-10 rounded-full bg-dark-700"></div>
                          <div>
                            <p className="font-medium">User1</p>
                            <p className="text-xs text-muted-foreground">user1@example.com</p>
                          </div>
                        </div>
                        <Button size="sm" variant="outline">View</Button>
                      </div>
                    </div>
                    <div className="p-4 border-t">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className="w-10 h-10 rounded-full bg-dark-700"></div>
                          <div>
                            <p className="font-medium">User2</p>
                            <p className="text-xs text-muted-foreground">user2@example.com</p>
                          </div>
                        </div>
                        <Button size="sm" variant="outline">View</Button>
                      </div>
                    </div>
                    <div className="p-4 border-t">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className="w-10 h-10 rounded-full bg-dark-700"></div>
                          <div>
                            <p className="font-medium">User3</p>
                            <p className="text-xs text-muted-foreground">user3@example.com</p>
                          </div>
                        </div>
                        <Button size="sm" variant="outline">View</Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="content">
              <Card>
                <CardHeader>
                  <CardTitle>Content Moderation</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="rounded-md border">
                    <div className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className="w-10 h-10 rounded-lg bg-dark-700"></div>
                          <div>
                            <p className="font-medium">Stream #1</p>
                            <p className="text-xs text-muted-foreground">by Creator1</p>
                          </div>
                        </div>
                        <div className="flex space-x-2">
                          <Button size="sm" variant="outline">Review</Button>
                          <Button size="sm" variant="destructive">Flag</Button>
                        </div>
                      </div>
                    </div>
                    <div className="p-4 border-t">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className="w-10 h-10 rounded-lg bg-dark-700"></div>
                          <div>
                            <p className="font-medium">NFT #1</p>
                            <p className="text-xs text-muted-foreground">by Creator2</p>
                          </div>
                        </div>
                        <div className="flex space-x-2">
                          <Button size="sm" variant="outline">Review</Button>
                          <Button size="sm" variant="destructive">Flag</Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="transactions">
              <Card>
                <CardHeader>
                  <CardTitle>Recent Transactions</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="rounded-md border">
                    <div className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">NFT Purchase</p>
                          <p className="text-xs text-muted-foreground">User1 purchased NFT #1</p>
                        </div>
                        <div className="text-right">
                          <p className="font-medium">1.2 DUSD</p>
                          <p className="text-xs text-muted-foreground">1 hour ago</p>
                        </div>
                      </div>
                    </div>
                    <div className="p-4 border-t">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">Creator Tip</p>
                          <p className="text-xs text-muted-foreground">User2 tipped Creator1</p>
                        </div>
                        <div className="text-right">
                          <p className="font-medium">0.5 DUSD</p>
                          <p className="text-xs text-muted-foreground">2 hours ago</p>
                        </div>
                      </div>
                    </div>
                    <div className="p-4 border-t">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">Stream Subscription</p>
                          <p className="text-xs text-muted-foreground">User3 subscribed to Creator2</p>
                        </div>
                        <div className="text-right">
                          <p className="font-medium">10 DUSD</p>
                          <p className="text-xs text-muted-foreground">3 hours ago</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>

        <div>
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>System Alerts</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg flex items-start">
                  <AlertTriangle className="h-5 w-5 text-yellow-500 mr-2 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-sm">High Network Load</p>
                    <p className="text-xs text-muted-foreground">
                      The platform is experiencing higher than normal traffic.
                    </p>
                  </div>
                </div>
                <div className="p-3 bg-primary-500/10 border border-primary-500/20 rounded-lg flex items-start">
                  <Settings className="h-5 w-5 text-primary-500 mr-2 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-sm">Maintenance Scheduled</p>
                    <p className="text-xs text-muted-foreground">
                      System maintenance scheduled for tomorrow at 2:00 AM UTC.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Button className="w-full justify-start" variant="outline" asChild>
                  <Link to="/admin/users">
                    <Users className="h-4 w-4 mr-2" />
                    Manage Users
                  </Link>
                </Button>
                <Button className="w-full justify-start" variant="outline" asChild>
                  <Link to="/admin/streams">
                    <Video className="h-4 w-4 mr-2" />
                    Manage Streams
                  </Link>
                </Button>
                <Button className="w-full justify-start" variant="outline" asChild>
                  <Link to="/admin/drops">
                    <Package className="h-4 w-4 mr-2" />
                    Manage Drops
                  </Link>
                </Button>
                <Button className="w-full justify-start" variant="outline" asChild>
                  <Link to="/admin/settings">
                    <Settings className="h-4 w-4 mr-2" />
                    Platform Settings
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
